/**
 * Configuration for measure labels based on environment settings
 */

// Type definitions for environment variables
interface ImportMetaEnv {
    readonly VITE_OUTCOME_REPORTS_SHOW_DEBUG_LABELS?: string;
    readonly VITE_ENV?: string;
}

// Check environment variables
const isDebugMode: boolean = import.meta.env.VITE_OUTCOME_REPORTS_SHOW_DEBUG_LABELS === 'true';
const isProduction: boolean = import.meta.env.VITE_ENV === 'production' || import.meta.env.VITE_ENV === 'staging';

export interface MeasureLabels {
    workStatusImprovedNew: string;
    returnToWorkOld: string;
    medicationReductionNew: string;
    medicationReductionOld: string;
}

export interface MeasureVisibility {
    showWorkStatusImprovedNew: boolean;
    showReturnToWorkOld: boolean;
    showMedicationReductionNew: boolean;
    showMedicationReductionOld: boolean;
}

export type BackendLabel = "Work Situation" | "Pain Medication" | "Work Status" | "Medication";

/**
 * Get the display labels based on the current environment
 * @returns MeasureLabels object with appropriate labels for the current environment
 */
export const getMeasureLabels = (): MeasureLabels => {
    if (isDebugMode && !isProduction) {
        // Debug mode for dev/qa environments
        return {
            workStatusImprovedNew: "Work Status Improved (New)",
            returnToWorkOld: "Return to Work (Old)",
            medicationReductionNew: "Medication Reduction (New)",
            medicationReductionOld: "Medication Reduction (Old)"
        };
    } else {
        // Production mode - hide the (New) and (Old) suffixes
        return {
            workStatusImprovedNew: "Work Status Improved",
            returnToWorkOld: "Return to Work",
            medicationReductionNew: "Medication Reduction",
            medicationReductionOld: "Medication Reduction"
        };
    }
};

/**
 * Determine which measures should be shown based on the data source
 *
 * @param hasNewWorkMeasure - Whether the data contains new Work Situation measure
 * @param hasOldWorkMeasure - Whether the data contains old Work Status measure
 * @param hasNewMedicationMeasure - Whether the data contains new Pain Medication measure
 * @param hasOldMedicationMeasure - Whether the data contains old Medication measure
 * @returns MeasureVisibility object indicating which measures should be displayed
 */
export const getVisibleMeasures = (
    hasNewWorkMeasure: boolean,
    hasOldWorkMeasure: boolean,
    hasNewMedicationMeasure: boolean,
    hasOldMedicationMeasure: boolean
): MeasureVisibility => {
    // In dev/qa with debug mode, show all measures that have data
    if (isDebugMode && !isProduction) {
        return {
            showWorkStatusImprovedNew: hasNewWorkMeasure,
            showReturnToWorkOld: hasOldWorkMeasure,
            showMedicationReductionNew: hasNewMedicationMeasure,
            showMedicationReductionOld: hasOldMedicationMeasure
        };
    }

    // In production/staging, show only the relevant format
    // If both formats exist, prefer the newer format
    return {
        showWorkStatusImprovedNew: hasNewWorkMeasure,
        showReturnToWorkOld: hasOldWorkMeasure && !hasNewWorkMeasure,
        showMedicationReductionNew: hasNewMedicationMeasure,
        showMedicationReductionOld: hasOldMedicationMeasure && !hasNewMedicationMeasure
    };
};

/**
 * Check if a measure contains data from the new question format
 * based on the backend label used
 * @param backendLabel - The label from the backend data
 * @returns true if the label corresponds to a new format question
 */
export const isNewFormatMeasure = (backendLabel: BackendLabel | string): boolean => {
    return backendLabel === "Work Situation" || backendLabel === "Pain Medication";
};

/**
 * Check if a measure contains data from the old question format
 * based on the backend label used
 * @param backendLabel - The label from the backend data
 * @returns true if the label corresponds to an old format question
 */
export const isOldFormatMeasure = (backendLabel: BackendLabel | string): boolean => {
    return backendLabel === "Work Status" || backendLabel === "Medication";
};